// Artist match — style-similar artists via precomputed style centroids.
// Seeded from the Search tab ("→ similar artists" on a seed) or a manual id.
import { useEffect, useState } from "preact/hooks";
import { api } from "../../api";
import { IMG_ROOT } from "../../lib/constants";
import type { Item } from "./Grid";

interface ArtistCard {
  userUuid: string;
  name: string;
  artworkCount: number;
  dist: number;
  samples: { id: number; title: string; lowResPath: string }[];
}

interface ArtistMatchProps {
  seedArtworkId: number | null;
  onSeed: (item: Item) => void;
}

export function ArtistMatch({ seedArtworkId, onSeed }: ArtistMatchProps) {
  const [manualId, setManualId] = useState("");
  const [artists, setArtists] = useState<ArtistCard[] | null>(null);
  const [status, setStatus] = useState(
    "seed an artwork from the Search tab (→ similar artists) or enter an artwork id"
  );

  async function run(id: number) {
    setStatus("computing style neighbours…");
    setArtists(null);
    try {
      const res = await api.search.similarArtists({ seedArtworkId: id });
      setArtists(res.artists);
      setStatus(`artists whose style is closest to the creator of #${id}`);
    } catch (e) {
      setStatus(`⚠ ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  useEffect(() => {
    if (seedArtworkId) void run(seedArtworkId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedArtworkId]);

  return (
    <div>
      <p class="ls-meta">
        Every artist's works are averaged into a "style centroid" — similar artists are a vector
        lookup away. Talent scouting: "who else paints like our bestseller?"
      </p>
      <form
        class="ls-row"
        onSubmit={(e) => {
          e.preventDefault();
          const id = parseInt(manualId, 10);
          if (id > 0) void run(id);
        }}
      >
        <input
          type="text"
          style="max-width:200px"
          placeholder="artwork id, e.g. 29860"
          value={manualId}
          onInput={(e) => setManualId((e.target as HTMLInputElement).value)}
        />
        <button type="submit">Find similar artists</button>
      </form>
      <p class="ls-meta">{status}</p>
      {artists?.map((a) => (
        <div key={a.userUuid} class="ls-artist">
          <div class="ls-artist-head">
            <b>{a.name}</b>
            <span class="ls-meta">
              {a.artworkCount} works · style distance {a.dist.toFixed(3)}
            </span>
          </div>
          <div class="ls-artist-samples">
            {a.samples.map((s) => (
              <img
                key={s.id}
                src={`${IMG_ROOT}${s.lowResPath}`}
                title={`${s.title} — click to search similar artworks`}
                onClick={() =>
                  onSeed({ id: s.id, title: s.title, lowResPath: s.lowResPath, dist: 0 })
                }
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
